import { range } from "lodash"
import { CCUtils } from "../../../../common/CCUtils"
import { Align, FontType } from "../../../../common/TextUtils"
import { ButtonContainer, Buttons } from "../../../generic-sprites/Buttons"
import { Rectangle } from "../../../generic-sprites/Shape"
import { PhaserText } from "../../../utils/PhaserText"
import { Scale } from "../../../utils/Scale"
import { Sprites } from "../../../utils/Sprites"
import { MakeAVideoStore } from "../MakeAVideoStore"
import { MakeVideoAtlases, MakeVideoAudio } from "../MakeVideoAssets"
import { MakeVideoLayoutTypes, MakeVideoSprite, MakeVideoSpriteContainer } from "../MakeVideoTypes"
import { MakeVideoBackgroundImage } from "./MakeVideoBackgroundSelectContainer"
import { MakeVideoLayoutTypeContainer } from "./MakeVideoLayoutTypeContainer"

export class MakeVideoVideoPlayerContainer implements MakeVideoSprite, MakeVideoSpriteContainer {
    active: boolean
    private readonly playButton: MakeVideoReviewPlayButton
    private frame: Rectangle
    private display: MakeVideoVideoDisplay

    constructor(
        game: Phaser.Game,
        group: Phaser.Group,
        x: number,
        y: number,
        edit: boolean,
        private nextButton: ButtonContainer,
        private editButton: ButtonContainer,
        debug: boolean
    ) {
        this.frame = new Rectangle(game, x, y, Scale.x(0.5), Scale.y(0.5), {lineWidth: 1})
        this.playButton = new MakeVideoReviewPlayButton(game, 1.003, x - Scale.x(0.001), this.frame.graphics.height + y, debug)
        this.display = new MakeVideoVideoDisplay(game, this.playButton, this.nextButton, this.editButton, edit, group, x, y, debug)
        this.playButton.setOnClick(this.display.play)
        this.active = true
    }

    destroy = () => {
        this.frame.destroy()
        this.playButton.destroy()
        this.display.destroy()
        this.active = false
    }

    disableAll = () => this.playButton.disable()

    enableAll = () => this.playButton.enable()
}

class MakeVideoVideoDisplay implements MakeVideoSprite {

    active: boolean
    private videos: Array<string>
    private audio: Phaser.Sound
    private background: MakeVideoBackgroundImage
    private layout: MakeVideoLayoutTypeContainer
    private text: Phaser.Text

    private static get demoVideoLineup(): Array<string> {
        return range(1, 9).map(index => `make-your-own-video-clip-${index}.mp4`)
    }

    constructor(
        private game: Phaser.Game,
        private playButton: MakeVideoReviewPlayButton,
        private nextButton: ButtonContainer,
        private editButton: ButtonContainer,
        private edit: boolean,
        private group: Phaser.Group,
        private x: number,
        private y: number,
        private debug: boolean
    ) {
        this.videos = CCUtils.debug && MakeAVideoStore.instance.videos.some(video => video.length === 0)
            ? MakeVideoVideoDisplay.demoVideoLineup
            : MakeAVideoStore.instance.videos
        this.audio = this.game.add.audio((CCUtils.debug && MakeAVideoStore.instance.music.length === 0
            ? MakeVideoAudio.POP
            : MakeAVideoStore.instance.music))
        this.active = true
    }

    play = () => {
        if (this.background && this.text) {
            this.background.destroy()
            this.text.destroy()
        }

        const timer = this.game.time.create(true)
        timer.add(0, this.createTitleImage)
        timer.add(2000, this.destroyTitleImage)
        timer.add(2000, () => this.playVideo(() => {
            this.audio.stop()
            this.endScreenSequence()
        }))
        this.audio.onPlay.add(() => {
            this.playButton.disable()
            this.editButton.disable()
            this.nextButton.disable()
            timer.start()
        })
        this.audio.loopFull()
        this.audio.play()
    }

    destroy = () => {
        if (this.background) {
            this.background.destroy()
        }
        if (this.text) {
            this.text.destroy()
        }
        this.active = false
    }

    private playVideo = (onComplete: () => void) => {
        const scale = Scale.scaleAmount() * 0.831
        const videos = this.videos.map(video => {
            const current = this.game.add.video(video)
            // Set time to beginning (just in case)
            current.video.currentTime = 0
            return current
        })
        const videoImages: Array<Phaser.Image> = []
        videos.forEach((video, index) => {
            if (index < 7) {
                video.onComplete.add(() => {
                    videoImages.push(videos[index + 1].addToWorld(this.x, this.y, 0, 0, scale, scale))
                    videos[index + 1].play()
                })
            } else {
                video.onComplete.add(() => {
                    videoImages.forEach(image => image.destroy())
                    videos.forEach(v => v.destroy())
                    onComplete()
                })
            }
        })
        videoImages.push(videos[0].addToWorld(this.x, this.y, 0, 0, scale, scale))
        videos[0].play()
    }

    private createTitleImage = () => {
        this.background = new MakeVideoBackgroundImage(this.game, MakeAVideoStore.instance.background || 1, 1, this.group, this.x, this.y, this.debug)
        this.layout = new MakeVideoLayoutTypeContainer(this.game, this.group, this.background, 32, MakeVideoLayoutTypes.LAYOUT_1)
    }

    private destroyTitleImage = () => {
        this.background.destroy()
        this.layout.destroy()
    }

    private endScreenSequence = () => {
        const finishedAudio = this.game.add.audio(this.edit ? MakeVideoAudio.FINISHED_VIDEO_NEXT : MakeVideoAudio.FINISHED_VIDEO_EDIT)
        this.background = new MakeVideoBackgroundImage(this.game, MakeAVideoStore.instance.background || 1, 1, this.group, this.x, this.y, this.debug)
        this.text = PhaserText.create(this.game, "THE END", {
            type: FontType.BODY,
            weight: "800",
            align: Align.CENTER,
        })
        Sprites.centerTextInSprite(this.background.sprite, this.text)
        finishedAudio.onStop.addOnce(() => {
            this.playButton.enable()
            this.nextButton.enable()
            if (!this.edit) {
                this.editButton.enable()
            }
        })
        finishedAudio.play()
    }
}

class MakeVideoReviewPlayButton implements MakeVideoSprite {
    active: boolean
    private readonly sprite: Phaser.Sprite
    private onClick: () => void

    constructor(
        game: Phaser.Game,
        scale: number,
        x: number,
        y: number,
        debug: boolean
    ) {
        this.sprite = Sprites.add(game, MakeVideoAtlases.PLAY_BUTTON, {x, y, scale, debug})
        this.active = true
    }

    setOnClick = (onClick: () => void) => this.onClick = onClick

    enable = () => {
        this.sprite.frame = 0
        if (this.onClick) {
            Buttons.makeSpriteButton(this.sprite, this.onClick)
        }
    }

    disable = () => {
        this.sprite.frame = 2
        if (this.onClick) {
            Buttons.undoSpriteButton(this.sprite)
        }
    }

    destroy = () => {
        this.sprite.destroy(true)
        this.active = false
    }
}
